/**
 * CounterCool Section Nav release.
 *
 * The nav is `position: sticky`, and sticky only holds inside its own parent.
 * The parent is the sections wrapper, so on its own the bar sticks to the end
 * of the page and rides over the Product Slider, where none of its links point.
 * https://developer.mozilla.org/en-US/docs/Web/CSS/position#sticky_positioning
 *
 * This file ends the stick at the bottom of the reviews, sliding the bar up by
 * however far the reviews have passed. The slide is a CSS transform that reads
 * `--aura-countercool-section-nav-release` in `countercool-section-nav.css`.
 *
 * That distance changes every frame, so a scroll listener beats an
 * IntersectionObserver: an observer reports that a crossing happened, not how
 * far past it the page sits. The listener only asks for a frame, so measuring
 * runs once per paint however often the scroll fires.
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/requestAnimationFrame
 */
(function () {
  const NAV_SELECTOR = '.aura-section--countercool-section-nav';
  const REVIEWS_SELECTOR = '.aura-section--product-reviews';
  const RELEASE_PROPERTY = '--aura-countercool-section-nav-release';

  const navEl = document.querySelector(NAV_SELECTOR);
  const reviewsEl = document.querySelector(REVIEWS_SELECTOR);

  // Do not proceed if either section is missing. With no reviews there is no
  // point to release at.
  if (!navEl || !reviewsEl) {
    return;
  }

  let framePending = false;
  let restingBottom = 0;

  /**
   * Measure where the stuck nav rests, in pixels from the top of the window.
   *
   * `getComputedStyle` and `offsetHeight` force style and layout work, so they
   * stay out of the scroll path and run only when a resize can change them.
   * https://web.dev/articles/avoid-large-complex-layouts-and-layout-thrashing
   *
   * @returns {void}
   */
  const measureNav = () => {
    // `top` is 0 on a phone and 20px from 768px up, so read it rather than
    // repeating either number.
    const stuckTop = parseFloat(getComputedStyle(navEl).top) || 0;

    restingBottom = stuckTop + navEl.offsetHeight;
  };

  /**
   * Slide the nav up by however far the reviews have scrolled past it.
   *
   * Once the bottom of the reviews rises above where the bar rests, the gap
   * between the two goes negative. Moving the bar by that gap holds its bottom
   * edge on that of the reviews until both leave the screen.
   *
   * @returns {void}
   */
  const setRelease = () => {
    framePending = false;

    const gap = reviewsEl.getBoundingClientRect().bottom - restingBottom;

    // Do not move the nav while the reviews still end below it. The bar is
    // either stuck at `top` or still scrolling towards it, and both are right.
    if (gap >= 0) {
      navEl.style.setProperty(RELEASE_PROPERTY, '0px');
      return;
    }

    // Stop once the bar clears the top of the window. Without this floor the
    // offset grows for the rest of the page, to no effect.
    const release = Math.max(gap, -restingBottom);

    navEl.style.setProperty(RELEASE_PROPERTY, `${release}px`);
  };

  /**
   * Ask for one measurement on the next frame.
   *
   * @returns {void}
   */
  const requestRelease = () => {
    // Do not queue a second frame while one waits. A scroll fires far more
    // often than the screen repaints.
    if (framePending) {
      return;
    }

    framePending = true;
    requestAnimationFrame(setRelease);
  };

  /**
   * Re-measure the nav, then place it again on the next frame.
   *
   * @returns {void}
   */
  const remeasure = () => {
    measureNav();
    requestRelease();
  };

  remeasure();

  window.addEventListener('scroll', requestRelease, { passive: true });

  // The reviews widget fills in after load and the slider settles with its
  // images, so the placement goes stale without a scroll. A ResizeObserver
  // reports every such change, and window resizes with them.
  // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
  //
  // Only the nav's own size feeds `measureNav`, so it gets the observer that
  // re-measures. The other two move the reviews without changing the nav, and
  // re-measuring for them would force the style and layout work this file was
  // written to keep out of the way.
  const navObserver = new ResizeObserver(remeasure);

  navObserver.observe(navEl);

  const positionObserver = new ResizeObserver(requestRelease);

  positionObserver.observe(reviewsEl);

  // The wrapper catches a section above the reviews growing, which moves the
  // reviews without resizing them.
  if (navEl.parentElement) {
    positionObserver.observe(navEl.parentElement);
  }
})();
